import { StudentRanking } from '../../domain/entities/StudentRanking';
import { LeaderboardEntry } from '../../domain/entities/LeaderboardEntry';
import { DailyStreak } from '../../domain/entities/DailyStreak';
import { XPBoost } from '../../domain/entities/XPBoost';
import { XPTransaction } from '../../domain/entities/XPTransaction';
import { WeeklyCycle } from '../../domain/entities/WeeklyCycle';
import {
  StudentRankingDTO,
  LeaderboardEntryDTO,
  LeaderboardDTO,
  DailyStreakDTO,
  XPBoostDTO,
  XPTransactionDTO,
  XPBreakdownDTO,
  WeeklyCycleDTO,
} from '../dto/RankingDTO';
import { LeagueTier } from '../../domain/value-objects/LeagueTier';
import { XPEventType, getXPEventLabel, getXPEventIcon } from '../../domain/value-objects/XPEventType';
import { BoostStatus, canActivateBoost } from '../../domain/value-objects/BoostStatus';

export class RankingMapper {
  static toStudentRankingDTO(
    ranking: StudentRanking,
    totalMembers: number
  ): StudentRankingDTO {
    return {
      id: ranking.id,
      studentId: ranking.studentId,
      studentName: ranking.studentName,
      studentAvatar: ranking.studentAvatar,
      leagueId: ranking.leagueId,
      tier: ranking.tier,
      weekXP: ranking.weekXP,
      totalXP: ranking.totalXP,
      lifetimeXP: ranking.lifetimeXP,
      inactiveCycles: ranking.inactiveCycles,
      position: ranking.position,
      previousPosition: ranking.previousPosition,
      positionChange: ranking.getPositionChange(),
      promotionStatus: ranking.getPromotionStatus(totalMembers),
      currentStreak: ranking.currentStreak,
      longestStreak: ranking.longestStreak,
      lessonsCompleted: ranking.lessonsCompleted,
      quizzesPassed: ranking.quizzesPassed,
      videosWatched: ranking.videosWatched,
      totalActivities: ranking.getTotalActivities(),
      weeklyCycleId: ranking.toPlainObject().weeklyCycleId,
      weekStartDate: ranking.weekStartDate.toISOString(),
      weekEndDate: ranking.weekEndDate.toISOString(),
      remainingDays: ranking.getRemainingDays(),
    };
  }

  static toLeaderboardEntryDTO(entry: LeaderboardEntry): LeaderboardEntryDTO {
    return {
      position: entry.position,
      studentId: entry.studentId,
      studentName: entry.studentName,
      studentAvatar: entry.studentAvatar,
      weekXP: entry.weekXP,
      currentStreak: entry.currentStreak,
      isCurrentUser: entry.isCurrentUser,
      promotionStatus: entry.promotionStatus,
      positionChange: entry.positionChange,
      xpDifferenceToAbove: entry.xpDifferenceToAbove,
      xpDifferenceToBelow: entry.xpDifferenceToBelow,
      medal: entry.getMedal(),
      isTopThree: entry.isTopThree(),
    };
  }

  static toLeaderboardDTO(
    entries: LeaderboardEntry[],
    leagueId: string,
    tier: LeagueTier,
    weeklyCycleId: string,
    cycle: WeeklyCycle
  ): LeaderboardDTO {
    const totalMembers = entries.length;
    const promotionThreshold = Math.ceil(totalMembers * 0.2);
    const demotionThreshold = Math.ceil(totalMembers * 0.15);

    return {
      leagueId,
      tier,
      weeklyCycleId,
      entries: entries.map((e) => this.toLeaderboardEntryDTO(e)),
      totalMembers,
      promotionThreshold,
      demotionThreshold,
      weekEndDate: cycle.endDate.toISOString(),
      remainingTime: cycle.getFormattedRemainingTime(),
    };
  }

  static toDailyStreakDTO(streak: DailyStreak | null): DailyStreakDTO {
    if (!streak) {
      return {
        currentStreak: 0,
        longestStreak: 0,
        lastActivityDate: new Date().toISOString(),
        isActive: false,
        hasActivityToday: false,
        bonusXP: 0,
        milestone: null,
        daysUntilNextMilestone: 7,
        streakDisplay: '0',
      };
    }

    return {
      currentStreak: streak.currentStreak,
      longestStreak: streak.longestStreak,
      lastActivityDate: streak.lastActivityDate.toISOString(),
      isActive: streak.isActive(),
      hasActivityToday: streak.hasActivityToday(),
      bonusXP: streak.getBonusXP(),
      milestone: streak.getMilestone(),
      daysUntilNextMilestone: streak.getDaysUntilNextMilestone(),
      streakDisplay: streak.getStreakDisplay(),
    };
  }

  static toXPBoostDTO(
    boost: XPBoost | null,
    hasUsedToday: boolean
  ): XPBoostDTO {
    if (!boost) {
      const status: BoostStatus = hasUsedToday ? 'USED_TODAY' : 'AVAILABLE';
      return {
        id: '',
        status,
        remainingSeconds: 0,
        remainingFormatted: '0:00',
        progressPercentage: 0,
        xpEarned: 0,
        canActivate: canActivateBoost(status),
      };
    }

    const status = boost.getStatus();
    return {
      id: boost.id,
      status: hasUsedToday && !boost.isActive() ? 'USED_TODAY' : status,
      activatedAt: boost.activatedAt.toISOString(),
      expiresAt: boost.expiresAt.toISOString(),
      remainingSeconds: boost.getRemainingSeconds(),
      remainingFormatted: boost.getFormattedTimeRemaining(),
      progressPercentage: boost.getProgressPercentage(),
      xpEarned: boost.xpEarned,
      canActivate: canActivateBoost(status) && !hasUsedToday,
    };
  }

  static toXPTransactionDTO(transaction: XPTransaction): XPTransactionDTO {
    return {
      id: transaction.id,
      eventType: transaction.eventType,
      baseXP: transaction.baseXP,
      multiplier: transaction.multiplier,
      finalXP: transaction.finalXP,
      isBoosted: transaction.isBoosted(),
      label: transaction.getLabel(),
      icon: transaction.getIcon(),
      displayText: transaction.getDisplayText(),
      timeSince: transaction.getTimeSince(),
      createdAt: transaction.createdAt.toISOString(),
    };
  }

  static toXPBreakdownDTO(
    breakdown: Record<XPEventType, number>
  ): XPBreakdownDTO {
    const totalXP = Object.values(breakdown).reduce((sum, xp) => sum + xp, 0);

    const bySource = Object.entries(breakdown)
      .filter(([, xp]) => xp > 0)
      .map(([source, xp]) => ({
        source: source as XPEventType,
        label: getXPEventLabel(source as XPEventType),
        icon: getXPEventIcon(source as XPEventType),
        totalXP: xp,
        percentage: totalXP > 0 ? Math.round((xp / totalXP) * 100) : 0,
      }))
      .sort((a, b) => b.totalXP - a.totalXP);

    return {
      bySource,
      totalXP,
    };
  }

  static toWeeklyCycleDTO(cycle: WeeklyCycle): WeeklyCycleDTO {
    return {
      id: cycle.id,
      workspaceId: cycle.workspaceId,
      startDate: cycle.startDate.toISOString(),
      endDate: cycle.endDate.toISOString(),
      isActive: cycle.isActive,
      remainingDays: cycle.getRemainingDays(),
      remainingFormatted: cycle.getFormattedRemainingTime(),
      progressPercentage: cycle.getProgressPercentage(),
      urgencyLevel: cycle.getUrgencyLevel(),
    };
  }
}
